import { useTranslation } from 'react-i18next';
import { Form, FormItem } from '../Ant';
import TextAreaField from '../textAreaField/textAreaField';
import { Slash } from '../icons';
import { usePage } from '../../context/PageContext';
import { useEffect, useState } from 'react';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useConflictCheck } from '../../context/ConflictCheckContext';
import { IConflictCheckParams } from '../../interface/conflictCheck';
import { ROUTES } from '../../constants/constants';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { useCreateConflictCheck } from '../../hooks/useCreateConflictCheck';
import { ICustomerType } from '../../constants/customer';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import CustomModal from '../informationPopup/customModal';
import { FormCard } from '../../assets/styles/components/formCard.styled';
import { isConflictCheckFormChanged } from './isConflictCheckFormChanged';
import ProcessedBySelect from './processedBySelect';
import PinnedSaveButton from '../pinnedSaveButton/pinnedSaveButton';

const CreateConflictCheck = () => {
  const { t } = useTranslation();
  const { goBack, push } = useNavigationStack();
  const location = useLocation();
  const { setTitle } = usePage();
  const { user } = useUser();
  const { trackPage } = useAnalytics();
  const createConflictCheckMutation = useCreateConflictCheck();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [onDiscardConfirm, setOnDiscardConfirm] = useState<(() => void) | null>(null);
  const { updateConflictCheckParams, conflictCheckParams } = useConflictCheck();

  useEffect(() => {
    trackPage(AnalyticsPage.CREATE_CONFLICT_CHECK);
  }, [trackPage]);
  const handleBack = () => {
    goBack();
    updateConflictCheckParams(null);
  };
  const onBack = () => {
    validateOnFormChanged(() => {
      handleBack();
    });
  };
  const validateOnFormChanged = (onProceed: () => void) => {
    const isFormChanged = isConflictCheckFormChanged(conflictCheckParams);
    setShowDiscardDialog(isFormChanged);
    setOnDiscardConfirm(() => onProceed);
    if (!isFormChanged) {
      onProceed?.();
    }
  };
  const handleDiscardConfirm = () => {
    onDiscardConfirm?.();
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };

  const handleDiscardCancel = () => {
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={onBack}
        showCross
        title={t('conflict:createConflictCheck')}
      />,
    );
  }, [conflictCheckParams]);
  useEffect(() => {
    if (location?.state?.customerId) {
      updateConflictCheckParams({
        ...conflictCheckParams,
        checkedByUserId: conflictCheckParams?.checkedByUserId ?? user?.id ?? '',
        checkedByUserName: conflictCheckParams?.checkedByUserName ?? user?.fullName ?? '',
        type: location?.state?.type,
        customerId: location?.state?.customerId,
      } as IConflictCheckParams);
    }
  }, []);

  const handleCreateConflictCheck = () => {
    const basePayload: IConflictCheckParams = {
      subject: conflictCheckParams?.subject ?? '',
      description: conflictCheckParams?.description ?? '',
      responsibleUserId: user?.id ?? '',
      checkedByUserId: conflictCheckParams?.checkedByUserId ?? '',
      ...(conflictCheckParams?.type === ICustomerType.COMPANY
        ? {
            principalCompanyId: conflictCheckParams?.customerId,
          }
        : {
            principalPrivatePersonId: conflictCheckParams?.customerId,
          }),
    };
    createConflictCheckMutation.mutate(
      {
        conflictCheckData: basePayload,
      },
      {
        onSuccess: () => {
          handleBack();
        },
      },
    );
  };
  const validateForm = () => {
    if (!conflictCheckParams?.subject || !conflictCheckParams?.checkedByUserId) {
      return true;
    }
    return false;
  };
  const handleSelectPerson = () => {
    push(ROUTES.SELECT_USER, {
      isFromConflictCheck: true,
      returnFocusTo: 'processedBy',
    });
  };
  return (
    <div className="h-full w-full pb-gutter add-transaction-form">
      <FormCard>
        <Form layout="vertical" className="w-full d-flex fd-column gap-gutter">
          <div className="w-full relative">
            <FormItem label={t('common:subject')} required>
              <TextAreaField
                value={conflictCheckParams?.subject ?? ''}
                maxLength={250}
                placeholder={t('common:addSubject')}
                onChange={(e) => {
                  updateConflictCheckParams({
                    ...conflictCheckParams,
                    subject: e.target.value,
                  } as IConflictCheckParams);
                }}
              />
            </FormItem>
          </div>
          <FormItem label={t('task:description')}>
            <TextAreaField
              value={conflictCheckParams?.description ?? ''}
              maxLength={1000}
              placeholder={t('common:addDescription')}
              onChange={(e) => {
                updateConflictCheckParams({
                  ...conflictCheckParams,
                  description: e.target.value,
                } as IConflictCheckParams);
              }}
            />
          </FormItem>
          <FormItem label={t('conflict:processedBy')} required={true}>
            <div className="relative">
              <ProcessedBySelect onOpenPicker={handleSelectPerson} />
            </div>
          </FormItem>
          <PinnedSaveButton
            data-testid="create-button"
            disabled={validateForm()}
            onClick={handleCreateConflictCheck}
          >
            {t('conflict:createConflictCheck')}
          </PinnedSaveButton>
        </Form>
      </FormCard>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </div>
  );
};

export default CreateConflictCheck;
